import { useState } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} SchoolQuestItemType
 * @property {number} id - The quest ID
 * @property {string} title - Quest title
 */

/**
 * @typedef {Object} SchoolQuestData
 * @property {{name: string, subdomain: string}} school - The school basic details
 * @property {SchoolQuestItemType[]} quests - List of quests assigned to the school
 * @property {string|null} last_modified_at - Last modified timestamp
 * @property {string|null} last_modified_by - Last modified by user
 */

/**
 * @typedef {Object} SchoolQuestListResponse
 * @property {boolean} success - Whether the request was successful
 * @property {[string, SchoolQuestData]} message - Tuple containing success message and school quest data
 * @property {null} data - Always null for this endpoint
 * @property {null} meta - Always null for this endpoint
 */

/**
 * Custom hook to fetch all quests assigned to a school
 *
 * @returns {{
 *   schoolQuestData: SchoolQuestData | undefined | null,
 *   loading: boolean,
 *   error: string,
 *   getSchoolQuestList: (schoolId: number | string) => Promise<void>
 * }}
 */
export const useGetSchoolQuestList = () => {
    const [schoolQuestData, setSchoolQuestData] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { fetchWithAuth } = useAuth();
    const apiUrl = process.env.REACT_APP_API_URL;

    /**
     * Fetches all quests assigned to a specific school
     * @param {number|string} schoolId - The ID of the school
     */
    const getSchoolQuestList = async (schoolId) => {
        if (!schoolId) {
            setError("School ID is required.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const response = await fetchWithAuth(`${apiUrl}/school/quest-access/${schoolId}`, {
                method: "GET",
            });

            if (!response.ok) throw new Error("Failed to fetch school quest list");

            /** @type {SchoolQuestListResponse} */
            const responseData = await response.json();

            setSchoolQuestData(responseData?.message?.[1] || null);

        } catch (err) {
            setSchoolQuestData(null);
            setError(err?.message || ERROR_MESSAGES.UNKNOWN);
        } finally {
            setLoading(false);
        }
    };

    return {
        schoolQuestData,
        loading,
        error,
        getSchoolQuestList,
    };
};